import React, { useCallback, useEffect, useState } from "react";
import { hotels } from "../api/endpoints";
import type { HotelProperty, Brand, ManagementCompany, RegionalRepresentative } from "../types";

export default function PropertiesPage() {
  const [properties, setProperties] = useState<HotelProperty[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [managementCompanies, setManagementCompanies] = useState<ManagementCompany[]>([]);
  const [regionalUsers, setRegionalUsers] = useState<RegionalRepresentative[]>([]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editProp, setEditProp] = useState<HotelProperty | null>(null);
  const [form, setForm] = useState({
    hotel_code: "", name: "", brand_id: 0, management_company_id: 0,
    company_name: "", contact_number: "", website: "", fax: "", address: "", city: "", state: "", zip_code: "",
    guest_rooms: 0, meeting_space_sqft: 0,
    regional_representative_id: 0,
    regional_representative_name: "", regional_representative_email: "",
    general_manager_name: "", property_manager_email: "",
    dos_name: "", dos_email: "",
    doc_name: "", doc_email: "",
    rvp_sales_email: "", rdc_email: "", rdo_email: "",
    additional_email_1: "", additional_email_2: "", additional_email_3: "",
    contract_ref: "", notes: "",
  });

  const blankForm = {
    hotel_code: "", name: "", brand_id: 0, management_company_id: 0,
    company_name: "", contact_number: "", website: "", fax: "", address: "", city: "", state: "", zip_code: "",
    guest_rooms: 0, meeting_space_sqft: 0,
    regional_representative_id: 0,
    regional_representative_name: "", regional_representative_email: "",
    general_manager_name: "", property_manager_email: "",
    dos_name: "", dos_email: "",
    doc_name: "", doc_email: "",
    rvp_sales_email: "", rdc_email: "", rdo_email: "",
    additional_email_1: "", additional_email_2: "", additional_email_3: "",
    contract_ref: "", notes: "",
  };

  const regionalOptions = [...regionalUsers]
    .sort((a, b) => {
      const aName = `${a.first_name || ""} ${a.last_name || ""}`.trim() || a.email;
      const bName = `${b.first_name || ""} ${b.last_name || ""}`.trim() || b.email;
      return aName.localeCompare(bName);
    });

  const loadProperties = useCallback(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    hotels.properties.list(params.toString()).then((res) => setProperties(res.items));
  }, [search]);

  useEffect(() => {
    loadProperties();
  }, [loadProperties]);

  useEffect(() => {
    let cancelled = false;

    const loadSupportingData = async () => {
      const [brandRows, managementCompanyRows, regionalRows] = await Promise.all([
        hotels.brands(),
        hotels.managementCompanies.list("limit=500"),
        hotels.regionalRepresentatives(),
      ]);
      if (cancelled) {
        return;
      }

      setBrands(brandRows);
      setManagementCompanies(managementCompanyRows.items);
      setRegionalUsers(regionalRows);
    };
    loadSupportingData();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editProp) {
      await hotels.properties.update(editProp.id, form);
    } else {
      await hotels.properties.create(form as any);
    }
    setShowForm(false);
    setEditProp(null);
    loadProperties();
  };

  const handleEdit = (p: HotelProperty) => {
    setEditProp(p);
    setForm({
      hotel_code: p.hotel_code, name: p.name, brand_id: p.brand?.id || 0,
      management_company_id: p.management_company?.id || 0,
      company_name: p.company_name || "",
      contact_number: p.contact_number, website: p.website || "", fax: p.fax || "", address: p.address,
      city: p.city, state: p.state, zip_code: p.zip_code,
      guest_rooms: p.guest_rooms, meeting_space_sqft: p.meeting_space_sqft,
      regional_representative_id: p.regional_representative_id || 0,
      regional_representative_name: p.regional_representative_name || "",
      regional_representative_email: p.regional_representative_email || "",
      general_manager_name: p.general_manager_name || "",
      property_manager_email: p.property_manager_email || "",
      dos_name: p.dos_name || "",
      dos_email: p.dos_email || "",
      doc_name: p.doc_name || "",
      doc_email: p.doc_email || "",
      rvp_sales_email: p.rvp_sales_email || "",
      rdc_email: p.rdc_email || "",
      rdo_email: p.rdo_email || "",
      additional_email_1: p.additional_email_1 || "",
      additional_email_2: p.additional_email_2 || "",
      additional_email_3: p.additional_email_3 || "",
      contract_ref: p.contract_ref || "",
      notes: p.notes || "",
    });
    setShowForm(true);
  };

  const handleRegionalRepresentativeChange = (value: number) => {
    const selectedUser = regionalUsers.find((user) => user.id === value);
    setForm({
      ...form,
      regional_representative_id: value,
      regional_representative_name:
        value === 0 ? form.regional_representative_name : `${selectedUser?.first_name || ""} ${selectedUser?.last_name || ""}`.trim(),
      regional_representative_email:
        value === 0 ? form.regional_representative_email : selectedUser?.email || "",
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Hotel Properties</h1>
        <button onClick={() => { setEditProp(null); setForm(blankForm); setShowForm(true); }} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
          Add Property
        </button>
      </div>

      <input
        placeholder="Search by code, name, or city..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="border rounded px-3 py-2 w-full mb-4"
      />

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">Code</th>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Brand</th>
              <th className="px-4 py-3 text-left">HMC</th>
              <th className="px-4 py-3 text-left">City/State</th>
              <th className="px-4 py-3 text-left">Rooms</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {properties.map((p) => (
              <tr key={p.id} className="border-t hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-xs">{p.hotel_code}</td>
                <td className="px-4 py-3">{p.name}</td>
                <td className="px-4 py-3">{p.brand?.name || "-"}</td>
                <td className="px-4 py-3 text-xs">{p.management_company?.name || "-"}</td>
                <td className="px-4 py-3">{p.city}{p.state ? `, ${p.state}` : ""}</td>
                <td className="px-4 py-3">{p.guest_rooms}</td>
                <td className="px-4 py-3">
                  <button onClick={() => handleEdit(p)} className="text-blue-600 hover:underline text-xs mr-2">Edit</button>
                  <button onClick={async () => { if (window.confirm("Deactivate?")) { await hotels.properties.delete(p.id); loadProperties(); } }} className="text-red-600 hover:underline text-xs">Deactivate</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[80vh] overflow-auto">
            <h2 className="text-lg font-bold mb-4">{editProp ? "Edit Property" : "Add Property"}</h2>
            <p className="mb-4 text-sm text-slate-500">
              Use this setup form to maintain hotel contacts, website, management company routing, and review recipients.
            </p>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Hotel Code" value={form.hotel_code} onChange={(e) => setForm({ ...form, hotel_code: e.target.value })} className="border rounded px-3 py-2" required disabled={!!editProp} />
                <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border rounded px-3 py-2" required />
                <select value={form.brand_id} onChange={(e) => setForm({ ...form, brand_id: Number(e.target.value) })} className="border rounded px-3 py-2">
                  <option value={0}>Select Brand</option>
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <select value={form.management_company_id} onChange={(e) => setForm({ ...form, management_company_id: Number(e.target.value) })} className="border rounded px-3 py-2">
                  <option value={0}>Select HMC</option>
                  {managementCompanies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
                </select>
                <input placeholder="Hotel Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Contact Number" value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Fax" value={form.fax} onChange={(e) => setForm({ ...form, fax: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Mgmt. Company Label (optional)" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="border rounded px-3 py-2 col-span-2" />
                <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Zip" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Guest Rooms" type="number" value={form.guest_rooms} onChange={(e) => setForm({ ...form, guest_rooms: Number(e.target.value) })} className="border rounded px-3 py-2" />
                <input placeholder="Meeting Space (sq ft)" type="number" value={form.meeting_space_sqft} onChange={(e) => setForm({ ...form, meeting_space_sqft: Number(e.target.value) })} className="border rounded px-3 py-2" />
                <select value={form.regional_representative_id} onChange={(e) => handleRegionalRepresentativeChange(Number(e.target.value))} className="border rounded px-3 py-2">
                  <option value={0}>Select Regional Rep</option>
                  {regionalOptions.map((user) => <option key={user.id} value={user.id}>{`${user.first_name} ${user.last_name}`.trim() || user.email}</option>)}
                </select>
                <input placeholder="Regional Rep Name" value={form.regional_representative_name} onChange={(e) => setForm({ ...form, regional_representative_name: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Regional Rep Email" value={form.regional_representative_email} onChange={(e) => setForm({ ...form, regional_representative_email: e.target.value })} className="border rounded px-3 py-2 col-span-2" />
                <input placeholder="GM Name" value={form.general_manager_name} onChange={(e) => setForm({ ...form, general_manager_name: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="GM Email" value={form.property_manager_email} onChange={(e) => setForm({ ...form, property_manager_email: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="DOS Name" value={form.dos_name} onChange={(e) => setForm({ ...form, dos_name: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="DOS Email" value={form.dos_email} onChange={(e) => setForm({ ...form, dos_email: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="DOC Name" value={form.doc_name} onChange={(e) => setForm({ ...form, doc_name: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="DOC Email" value={form.doc_email} onChange={(e) => setForm({ ...form, doc_email: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="RVP Sales Email" value={form.rvp_sales_email} onChange={(e) => setForm({ ...form, rvp_sales_email: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="RDC Email" value={form.rdc_email} onChange={(e) => setForm({ ...form, rdc_email: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="RDO Email" value={form.rdo_email} onChange={(e) => setForm({ ...form, rdo_email: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Other Email 1" value={form.additional_email_1} onChange={(e) => setForm({ ...form, additional_email_1: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Other Email 2" value={form.additional_email_2} onChange={(e) => setForm({ ...form, additional_email_2: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Other Email 3" value={form.additional_email_3} onChange={(e) => setForm({ ...form, additional_email_3: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Contract Ref / Internal Tag" value={form.contract_ref} onChange={(e) => setForm({ ...form, contract_ref: e.target.value })} className="border rounded px-3 py-2" />
                <textarea placeholder="Competition Comments / Property Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border rounded px-3 py-2 col-span-2 min-h-[120px]" />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
